import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

/**
 * "Bereit für das nächste Level"-CTA-Sektion auf der Landing Page.
 * Ersetzt die alten Kundenbewertungen — fokussiert die Conversion direkt
 * auf My-World-Konto-Anlage. Großes glühendes My-World-Logo ist der
 * Blickfang.
 */
export default function NextLevelCta() {
  return (
    <section
      className="relative py-24 sm:py-32 overflow-hidden bg-[#0a0a0a]"
      data-testid="next-level-cta"
    >
      {/* Hintergrund-Glow */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full blur-3xl opacity-30"
          style={{ background: 'radial-gradient(circle, #0094ff 0%, transparent 60%)' }}
        />
        <div
          className="absolute top-1/4 right-1/4 w-[400px] h-[400px] rounded-full blur-3xl opacity-20"
          style={{ background: 'radial-gradient(circle, #a855f7 0%, transparent 70%)' }}
        />
      </div>

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight"
          data-testid="next-level-headline"
        >
          Bereit für das nächste Level bei One World?
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="text-base sm:text-lg text-[#a1a1aa] mt-4"
        >
          Willkommen bei
        </motion.p>

        {/* Logo mit Glow */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.25, type: 'spring', stiffness: 100 }}
          className="relative inline-block my-8 sm:my-10"
        >
          {/* mehrlagiger Glow */}
          <div
            className="absolute inset-0 blur-3xl opacity-60"
            style={{ background: 'radial-gradient(ellipse, #0094ff 0%, #a855f7 40%, transparent 70%)' }}
            aria-hidden="true"
          />
          <div
            className="absolute inset-0 blur-2xl opacity-50 animate-pulse"
            style={{ background: 'radial-gradient(circle, #0094ff 0%, transparent 60%)' }}
            aria-hidden="true"
          />
          <img
            src="/myworld-logo.png"
            alt="My World"
            className="relative w-64 sm:w-80 lg:w-96 mx-auto drop-shadow-[0_0_40px_rgba(0,148,255,0.6)]"
            data-testid="next-level-logo"
          />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-base sm:text-lg lg:text-xl text-white max-w-2xl mx-auto leading-relaxed"
          data-testid="next-level-copy"
        >
          Erstelle dir jetzt ein kostenloses Konto und entdecke unglaubliche
          Features &amp; Funktionen — von der KI-Pflegeberatung über deine
          digitale Garage bis zum Premium-Concierge.
        </motion.p>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.55 }}
          className="mt-10"
        >
          <Link
            to="/login?redirect=/my-world"
            data-testid="next-level-cta-button"
            className="group inline-flex items-center gap-2 bg-gradient-to-r from-[#0094ff] to-[#0072c6] hover:from-[#0072c6] hover:to-[#005494] text-white text-base sm:text-lg font-bold px-8 py-4 rounded-2xl shadow-[0_8px_32px_-4px_rgba(0,148,255,0.6)] transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
          >
            Jetzt Konto erstellen / Einloggen
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>

          <p className="text-xs text-[#605e5e] mt-4">
            Kostenlos · keine Kreditkarte · in unter 60 Sekunden startklar
          </p>
        </motion.div>
      </div>
    </section>
  );
}
